<script setup lang="ts">
import { ref } from 'vue'
import { PathsCanvas } from '@/components/ui/background-paths'
import { pathPresets, type PresetName } from '@/components/ui/background-paths/presets'

const count = ref(24)
const segmentsPerPath = ref(3)
const speed = ref(1)
const batonLengthRatio = ref(0.22)
const widthBase = ref(0.6)
const widthIncrement = ref(0.03)
const baseOpacity = ref(0.12)
const opacityIncrement = ref(0.01)
const spread = ref(6)
const curveOffset = ref(60)
const curveSegments = ref(3)
const outflowMargin = ref(200)
const mirrored = ref(true)
const interactive = ref(true)
const colorClass = ref('text-slate-200/60 dark:text-white/40')
const selectedPreset = ref<PresetName | ''>('')

function applyPreset(name: PresetName) {
  const p = pathPresets[name]?.config
  if (!p) return
  if (p.count != null) count.value = p.count
  if (p.segmentsPerPath != null) segmentsPerPath.value = p.segmentsPerPath
  if (p.speed != null) speed.value = p.speed
  if (p.batonLengthRatio != null) batonLengthRatio.value = p.batonLengthRatio
  if (p.widthBase != null) widthBase.value = p.widthBase
  if (p.widthIncrement != null) widthIncrement.value = p.widthIncrement
  if (p.baseOpacity != null) baseOpacity.value = p.baseOpacity
  if (p.opacityIncrement != null) opacityIncrement.value = p.opacityIncrement
  if (p.spread != null) spread.value = p.spread
  if (p.curveOffset != null) curveOffset.value = p.curveOffset
  if (p.curveSegments != null) curveSegments.value = p.curveSegments
  if (p.outflowMargin != null) outflowMargin.value = p.outflowMargin
  if (p.mirrored != null) mirrored.value = p.mirrored
  if (p.colorClass != null) colorClass.value = p.colorClass
}
</script>

<template>
  <div class="min-h-screen w-full bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white relative">
    <ClientOnly>
      <PathsCanvas
        class="text-slate-950 dark:text-white"
        :count="count"
        :segments-per-path="segmentsPerPath"
        :speed="speed"
        :baton-length-ratio="batonLengthRatio"
        :width-base="widthBase"
        :width-increment="widthIncrement"
        :base-opacity="baseOpacity"
        :opacity-increment="opacityIncrement"
        :spread="spread"
        :curve-offset="curveOffset"
        :curve-segments="curveSegments"
        :outflow-margin="outflowMargin"
        :mirrored="mirrored"
        :interactive="interactive"
        :color-class="colorClass"
      />
    </ClientOnly>

    <div class="relative z-10 container mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="gap-y-3 md:col-span-2">
        <h2 class="text-lg font-semibold">Presets</h2>
        <div class="flex gap-3 items-center">
          <select v-model="selectedPreset" class="border rounded px-2 py-1 bg-transparent">
            <option value="">Custom</option>
            <option v-for="(p, key) in pathPresets" :key="key" :value="key">{{ p.name }}</option>
          </select>
          <button class="px-3 py-1 border rounded" @click="selectedPreset && applyPreset(selectedPreset as PresetName)">Apply</button>
        </div>
      </div>
      <div class="gap-y-3">
        <h2 class="text-lg font-semibold">Density</h2>
        <label class="block">Count: {{ count }} <input class="w-full" type="range" min="8" max="48" step="1" v-model.number="count" /></label>
        <label class="block">Segments per path: {{ segmentsPerPath }} <input class="w-full" type="range" min="1" max="6" step="1" v-model.number="segmentsPerPath" /></label>
        <label class="block">Speed: {{ speed.toFixed(2) }}x <input class="w-full" type="range" min="0.25" max="3" step="0.05" v-model.number="speed" /></label>
        <label class="block">Baton length: {{ (batonLengthRatio*100).toFixed(0) }}% <input class="w-full" type="range" min="0.05" max="0.5" step="0.01" v-model.number="batonLengthRatio" /></label>
        <label class="inline-flex items-center gap-2"><input type="checkbox" v-model="mirrored" /> Mirrored</label>
        <label class="inline-flex items-center gap-2"><input type="checkbox" v-model="interactive" /> Interactive handles</label>
      </div>
      <div class="gap-y-3">
        <h2 class="text-lg font-semibold">Style</h2>
        <label class="block">Width base: {{ widthBase.toFixed(2) }} <input class="w-full" type="range" min="0.2" max="2" step="0.02" v-model.number="widthBase" /></label>
        <label class="block">Width increment: {{ widthIncrement.toFixed(3) }} <input class="w-full" type="range" min="0" max="0.08" step="0.002" v-model.number="widthIncrement" /></label>
        <label class="block">Base opacity: {{ baseOpacity.toFixed(2) }} <input class="w-full" type="range" min="0.02" max="0.3" step="0.01" v-model.number="baseOpacity" /></label>
        <label class="block">Opacity increment: {{ opacityIncrement.toFixed(3) }} <input class="w-full" type="range" min="0" max="0.03" step="0.001" v-model.number="opacityIncrement" /></label>
        <label class="block">Spread: {{ spread }} <input class="w-full" type="range" min="0" max="20" step="1" v-model.number="spread" /></label>
        <label class="block">Curve offset: {{ curveOffset }} <input class="w-full" type="range" min="-200" max="200" step="2" v-model.number="curveOffset" /></label>
        <label class="block">Curve segments: {{ curveSegments }} <input class="w-full" type="range" min="1" max="6" step="1" v-model.number="curveSegments" /></label>
        <label class="block">Outflow margin: {{ outflowMargin }} <input class="w-full" type="range" min="0" max="300" step="10" v-model.number="outflowMargin" /></label>
      </div>
    </div>
  </div>
</template>
